// src/components/LinkToFacilityResponseModal.jsx
import React, { useEffect, useMemo, useState } from "react";
import {
    Dialog, DialogTitle, DialogContent, DialogActions,
    Button, Stack, Typography, Alert, Collapse, IconButton,
    Card, CardContent, Chip, TextField, MenuItem
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";

import { useAuth } from "../components/TokenHandler";

const DEV = process.env.NODE_ENV !== "production";

// ---- helpers ----
const toItemsArray = (data) => {
    if (!data) return [];
    if (Array.isArray(data)) return data;
    if (Array.isArray(data.items)) return data.items;
    if (Array.isArray(data.rows)) return data.rows;
    return [data];
};

const show = (d) => {
    if (!d && d !== 0) return "";
    const s = String(d);
    const m = s.match(/^(\d{4}-\d{2}-\d{2})/);
    return m ? m[1] : s;
};

const seedTopLevelDates = (row) => ({
    start_date: row?.start_date ?? "",
    on_hold_date: row?.on_hold_date ?? "",
    end_date: row?.end_date ?? "",
});

// Prefer OUID; fallback to facility_unique_id
const facKey = (f) => f?.ouid || String(f?.facility_unique_id || "");
const findFacilityByPick = (row, pick) => {
    const facs = row?.link_conflict?.suggested_facilities?.facilities || [];
    return facs.find((f) => facKey(f) === pick) || null;
};

export default function LinkToFacilityResponseModal({
                                                        open,
                                                        onClose,
                                                        response,
                                                        onResubmit,          // (updatedItemsArray) => void
                                                        mode = "accept",     // "accept" | "deny"
                                                    }) {


    const {
        keycloak: kc,
        authenticated,
        infoMessage,
        setInfoMessage,
        givenName,
        email,
        roles,
    } = useAuth();
    const items = useMemo(() => toItemsArray(response), [response]);

    // per-row editable top-level dates
    const [edits, setEdits] = useState({});
    // per-row chosen facility (value = facKey)
    const [linkPick, setLinkPick] = useState({});
    // per-row explicit user actions
    //   linkAccepted: user clicked "Mark link resolved"
    //   dateAccepted: user clicked "Accept suggested dates"
    const [actions, setActions] = useState({});
    // rows excluded from this re-submit (removed from batch)
    const [excluded, setExcluded] = useState({});
    // DEV view
    const [showRaw, setShowRaw] = useState(false);

    // Re-seed local editable state when items change
    useEffect(() => {
        const nextEdits = {};
        const nextPicks = { ...linkPick };
        const nextActions = { ...actions };

        for (const row of items) {
            nextEdits[row.id] = seedTopLevelDates(row);

            // Only seed facility picker if the backend returned suggested_facilities.
            const hasFacilitiesData = !!row?.link_conflict?.suggested_facilities;
            if (hasFacilitiesData) {
                const facs = row.link_conflict.suggested_facilities.facilities || [];
                if (facs.length > 0) {
                    const existingPick = linkPick[row.id];
                    if (existingPick && findFacilityByPick(row, existingPick)) {
                        nextPicks[row.id] = existingPick;
                    } else if (facs.length === 1) {
                        nextPicks[row.id] = facKey(facs[0]);
                    } else if (!nextPicks[row.id]) {
                        nextPicks[row.id] = "";
                    }
                } else {
                    // backend responded but list is empty; keep prior pick (usually "")
                    nextPicks[row.id] = nextPicks[row.id] || "";
                }
            } else {
                // No facilities yet (e.g., first deny+dates before POST). Keep whatever the user had.
                if (typeof nextPicks[row.id] === "undefined") nextPicks[row.id] = "";
            }

            // reflect server-accepted conflicts (if any)
            const linkAcceptedServer = row?.link_conflict?.status_of_conflict === "accepted";
            const dateAcceptedServer = row?.date_conflict?.status_of_conflict === "accepted";

            nextActions[row.id] = {
                linkAccepted: linkAcceptedServer || nextActions[row.id]?.linkAccepted || false,
                dateAccepted: dateAcceptedServer || nextActions[row.id]?.dateAccepted || false,
            };
        }

        setEdits(nextEdits);
        setLinkPick(nextPicks);
        setActions(nextActions);
        // do not include linkPick/actions in deps to avoid clobbering user input mid-edit
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [items]);

    // convenience selectors
    const handleDateChange = (rowId, field, value) => {
        setEdits((prev) => ({
            ...prev,
            [rowId]: { ...(prev[rowId] || {}), [field]: value },
        }));
    };
    const handlePickChange = (rowId, value) => {
        setLinkPick((prev) => ({ ...prev, [rowId]: value }));
    };
    const markLinkResolved = (row) => {
        setActions((prev) => ({
            ...prev,
            [row.id]: { ...(prev[row.id] || {}), linkAccepted: true },
        }));
    };
    const acceptSuggestedDates = (row) => {
        setActions((prev) => ({
            ...prev,
            [row.id]: { ...(prev[row.id] || {}), dateAccepted: true },
        }));
    };
    const removeFromBatch = (row) => {
        setExcluded((prev) => ({ ...prev, [row.id]: true }));
    };
    const restoreToBatch = (row) => {
        setExcluded((prev) => {
            const n = { ...prev };
            delete n[row.id];
            return n;
        });
    };

    // Rows that truly block re-submit: ONLY if backend returned suggested_facilities AND the list is empty
    const blockingRows = useMemo(() => {
        return items.filter((row) => {
            if (excluded[row.id]) return false;
            const hasFacilitiesData = !!row?.link_conflict?.suggested_facilities;
            const facs = hasFacilitiesData
                ? (row.link_conflict.suggested_facilities.facilities || [])
                : null;
            return hasFacilitiesData && Array.isArray(facs) && facs.length === 0;
        });
    }, [items, excluded]);

    const nothing = !items || items.length === 0;
    const disableResubmit = nothing || blockingRows.length > 0;

    // Build outgoing payload and hand back to parent
    const handleResolveAndResubmit = async () => {
        // Apply edits and explicit conflict acceptances.
        const outgoing = items
            .filter((row) => !excluded[row.id])
            .map((row) => {
                const e = edits[row.id] || {};
                const act = actions[row.id] || {};
                const pick = linkPick[row.id];

                const next = {
                    ...row,
                    start_date: e.start_date ?? row.start_date ?? "",
                    on_hold_date: e.on_hold_date ?? row.on_hold_date ?? "",
                    end_date: e.end_date ?? row.end_date ?? "",
                };

                // Mark link_conflict accepted ONLY if user clicked the button and a pick is selected.
                if (act.linkAccepted && pick && row?.link_conflict?.suggested_facilities) {
                    const chosen = findFacilityByPick(row, pick);
                    next.link_conflict = {
                        ...(row.link_conflict || {}),
                        status_of_conflict: "accepted",
                        suggested_facilities: {
                            ...(row.link_conflict?.suggested_facilities || {}),
                            pending: false,
                            facilities: chosen ? [chosen] : [],
                        },
                    };
                }

                // Mark date_conflict accepted ONLY if user clicked the button.
                if (act.dateAccepted && row?.date_conflict) {
                    next.date_conflict = {
                        ...row.date_conflict,
                        status_of_conflict: "accepted",
                        new_start_date: row.date_conflict.new_start_date ?? null,
                        new_end_date: row.date_conflict.new_end_date ?? null,
                        new_hold_date: row.date_conflict.new_hold_date ?? null,
                    };
                }

                // IMPORTANT: do NOT set or change linked_to here; backend handles it.

                return next;
            });

        // After exclusions, if any remaining rows have an *empty* facilities list that came from the server, still block.
        const stillBlocking = outgoing.some((row) => {
            const hasFacilitiesData = !!row?.link_conflict?.suggested_facilities;
            const facs = hasFacilitiesData
                ? (row.link_conflict.suggested_facilities.facilities || [])
                : null;
            return hasFacilitiesData && Array.isArray(facs) && facs.length === 0;
        });
        if (stillBlocking) return;

        if (typeof onResubmit === "function") {
            await onResubmit(outgoing);
        }
    };

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="xl"
            fullWidth
            scroll="paper"
            PaperProps={{ sx: { display: "flex", flexDirection: "column", height: "90vh" } }}
        >
            <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                Link to Facility — {mode === "deny" ? "Deny + Dates" : "Accept Flow"}
                <IconButton aria-label="close" onClick={onClose} sx={{ ml: "auto" }} size="small">
                    <CloseIcon />
                </IconButton>
            </DialogTitle>

            <DialogContent dividers sx={{ p: 2 }}>
                {/* DEV raw view toggle */}
                {DEV && (
                    <>
                        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
                            <IconButton size="small" onClick={() => setShowRaw((s) => !s)}>
                                {showRaw ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                            </IconButton>
                            <Typography variant="body2" color="text.secondary">View raw response JSON</Typography>
                        </Stack>
                        <Collapse in={showRaw} timeout="auto" unmountOnExit>
              <pre
                  style={{
                      margin: 0, padding: 12, background: "#0f172a", color: "#e2e8f0",
                      borderRadius: 8, fontSize: 12, overflowX: "auto"
                  }}
              >
                {JSON.stringify(response, null, 2)}
              </pre>
                        </Collapse>
                    </>
                )}

                {/* Global banner: only when backend returned empty facilities for some rows */}
                {blockingRows.length > 0 && (
                    <Alert severity="warning" sx={{ mb: 2 }}>
                        {blockingRows.length} row{blockingRows.length === 1 ? "" : "s"} have no suggested
                        sub-facilities. Remove them from the batch or retry later after the backend can suggest facilities.
                    </Alert>
                )}

                {nothing ? (
                    <Alert severity="info" sx={{ mt: 1 }}>No items in response.</Alert>
                ) : (
                    <Stack spacing={2}>
                        {items.map((row) => {
                            const e = edits[row.id] || {};
                            const act = actions[row.id] || {};
                            const hasFacilitiesData = !!row?.link_conflict?.suggested_facilities;
                            const facs = hasFacilitiesData
                                ? (row.link_conflict.suggested_facilities.facilities || [])
                                : null; // null → not fetched yet (first deny+dates path)
                            const pickVal = linkPick[row.id] ?? "";

                            const badge =
                                row?.date_conflict?.status_of_conflict ||
                                row?.link_conflict?.status_of_conflict ||
                                "editable";

                            const linkNeedsAction = !!row?.link_conflict && !act.linkAccepted;
                            const dateNeedsAction = !!row?.date_conflict && !act.dateAccepted;

                            const isExcluded = !!excluded[row.id];

                            return (
                                <Card key={row.id} variant="outlined" sx={isExcluded ? { opacity: 0.5 } : undefined}>
                                    <CardContent>
                                        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                                            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                                                {row.service_name} • itemid: {row.itemid || "n/a"}
                                            </Typography>
                                            <Chip
                                                size="small"
                                                label={
                                                    row?.date_conflict || row?.link_conflict
                                                        ? `conflict: ${badge}`
                                                        : mode === "deny" ? "deny+dates" : "review"
                                                }
                                                color={badge === "accepted" ? "success" : badge === "pending" ? "warning" : "default"}
                                                variant="outlined"
                                            />
                                        </Stack>

                                        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                                            <strong>Facility:</strong> {row.facility_name || "—"}
                                        </Typography>

                                        {/* Server's suggested date change (read-only) */}
                                        {row?.date_conflict && (
                                            <Typography variant="body2" sx={{ mb: 1 }}>
                                                <strong>Server suggests:</strong>{" "}
                                                start={show(row.date_conflict.new_start_date)} • hold={show(row.date_conflict.new_hold_date)} • end={show(row.date_conflict.new_end_date)}{" "}
                                                ({row.date_conflict.status_of_conflict})
                                            </Typography>
                                        )}

                                        {/* Date editors */}
                                        <Stack direction={{ xs: "column", sm: "row" }} spacing={2} sx={{ mb: 1 }}>
                                            <TextField
                                                label="New start date"
                                                type="date"
                                                size="small"
                                                InputLabelProps={{ shrink: true }}
                                                value={show(e.start_date)}
                                                onChange={(ev) => handleDateChange(row.id, "start_date", ev.target.value || "")}
                                                disabled={isExcluded}
                                            />
                                            <TextField
                                                label="New hold date"
                                                type="date"
                                                size="small"
                                                InputLabelProps={{ shrink: true }}
                                                value={show(e.on_hold_date)}
                                                onChange={(ev) => handleDateChange(row.id, "on_hold_date", ev.target.value || "")}
                                                disabled={isExcluded}
                                            />
                                            <TextField
                                                label="New end date"
                                                type="date"
                                                size="small"
                                                InputLabelProps={{ shrink: true }}
                                                value={show(e.end_date)}
                                                onChange={(ev) => handleDateChange(row.id, "end_date", ev.target.value || "")}
                                                disabled={isExcluded}
                                            />
                                        </Stack>

                                        {/* Link conflict resolver UI:
                        - If backend hasn't returned suggestions yet → show nothing (allow submit).
                        - If returned and non-empty → show picker + button.
                        - If returned and EMPTY → warn + "Remove from batch" button. */}
                                        {hasFacilitiesData && Array.isArray(facs) && facs.length > 0 ? (
                                            <Stack direction={{ xs: "column", sm: "row" }} spacing={2} sx={{ mb: 1 }}>
                                                <TextField
                                                    select
                                                    size="small"
                                                    label="Choose sub-facility"
                                                    value={pickVal}
                                                    onChange={(e) => handlePickChange(row.id, e.target.value)}
                                                    helperText="Pick exactly one facility; click 'Mark link resolved' to accept."
                                                    sx={{ minWidth: 360 }}
                                                    disabled={isExcluded}
                                                >
                                                    <MenuItem value="">(Select one)</MenuItem>
                                                    {facs.map((f) => (
                                                        <MenuItem key={facKey(f)} value={facKey(f)}>
                                                            {f.facility_name} — {f.city}, {f.state}
                                                        </MenuItem>
                                                    ))}
                                                </TextField>

                                                <Button
                                                    variant="outlined"
                                                    disabled={!pickVal || !linkNeedsAction || isExcluded}
                                                    onClick={() => markLinkResolved(row)}
                                                >
                                                    Mark link resolved
                                                </Button>
                                            </Stack>
                                        ) : hasFacilitiesData && Array.isArray(facs) && facs.length === 0 ? (
                                            <>
                                                <Alert severity="warning" sx={{ mb: 1 }}>
                                                    No suggested sub-facilities were returned for this row. It cannot be processed.
                                                </Alert>
                                                {!isExcluded ? (
                                                    <Button
                                                        variant="outlined"
                                                        color="warning"
                                                        onClick={() => removeFromBatch(row)}
                                                        sx={{ mb: 1 }}
                                                    >
                                                        Remove from batch
                                                    </Button>
                                                ) : (
                                                    <Button
                                                        variant="text"
                                                        onClick={() => restoreToBatch(row)}
                                                        sx={{ mb: 1 }}
                                                    >
                                                        Restore to batch
                                                    </Button>
                                                )}
                                            </>
                                        ) : null}

                                        {/* Date conflict explicit accept */}
                                        {row?.date_conflict && (
                                            <Stack direction="row" spacing={2} sx={{ mb: 1 }}>
                                                <Button
                                                    variant="outlined"
                                                    disabled={!dateNeedsAction || isExcluded}
                                                    onClick={() => acceptSuggestedDates(row)}
                                                >
                                                    Override current database dates
                                                </Button>
                                            </Stack>
                                        )}

                                        {row?.changes && row.changes.length > 0 && (
                                            <Typography variant="caption" color="text.secondary">
                                                Change logged: {row.changes[0]?.accepted} • change_id {row.changes[0]?.change_id}
                                            </Typography>
                                        )}
                                    </CardContent>
                                </Card>
                            );
                        })}
                    </Stack>
                )}
            </DialogContent>

            <DialogActions
                sx={{
                    justifyContent: "space-between",
                    px: 2,
                    py: 1,
                    borderTop: (t) => `1px solid ${t.palette.divider}`,
                }}
            >
                <Typography variant="body2" color="text.secondary">
                    {items.length} row{items.length === 1 ? "" : "s"}
                </Typography>

                <Stack direction="row" spacing={1}>
                    <Button onClick={onClose}>Close</Button>
                    <Button
                        onClick={handleResolveAndResubmit}
                        variant="contained"
                        disabled={disableResubmit}
                    >
                        Resolve All &amp; Resubmit
                    </Button>
                </Stack>
            </DialogActions>
        </Dialog>
    );
}
